import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MetricHistoryPoint } from '../../models/apiTypes';
import ModalFrame from '../primitives/ModalFrame';


/**
 * Town-hall metric chart components.
 *
 * - MetricChartModal: full-screen detailed chart built ON TOP of ModalFrame
 *   (parameterized by title/series — single reusable component, no modal forks).
 *   Rendered through a portal to document.body so the backdrop dims the WHOLE
 *   screen even when the opener lives inside a transformed/high-z container
 *   (CSS transform creates a containing block for position:fixed descendants).
 * - MetricTile: compact stat tile (label + value); tapping it opens the modal.
 *
 * The chart is hand-rolled SVG (no recharts/chart.js/echarts).
 */

// ---------------------------------------------------------------------------
// Full-screen chart modal (built on ModalFrame)
// ---------------------------------------------------------------------------

interface MetricChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  series: MetricHistoryPoint[];
  /** Current (live) value, shown prominently above the chart. */
  currentValue?: number;
  /** Format a numeric value for display (e.g. round / add unit). */
  formatValue?: (v: number) => string;
  color?: string;
  /** Optional text-link action rendered below the chart (e.g. "Go to a free plot"). */
  actionLabel?: string;
  onAction?: () => void;
}

const CHART_W = 480;
const CHART_H = 220;
const CHART_PAD_L = 44;
const CHART_PAD_R = 12;
const CHART_PAD_T = 16;
const CHART_PAD_B = 28;

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // DD.MM
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}`;
}

export function MetricChartModal({
  isOpen,
  onClose,
  title,
  description,
  series,
  currentValue,
  formatValue = (v) => String(Math.round(v * 100) / 100),
  color = 'var(--color-brand)',
  actionLabel,
  onAction,
}: MetricChartModalProps) {
  const { t } = useTranslation();

  const chart = useMemo(() => {
    if (!Array.isArray(series) || series.length === 0) return null;
    const values = series.map((p) => Number(p?.value)).filter((v) => Number.isFinite(v));
    if (values.length === 0) return null;
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (min === max) {
      // Flat series — pad the range so the line sits in the middle.
      min = min - 1;
      max = max + 1;
    }
    const span = max - min || 1;
    const innerW = CHART_W - CHART_PAD_L - CHART_PAD_R;
    const innerH = CHART_H - CHART_PAD_T - CHART_PAD_B;
    const n = series.length;
    const stepX = n > 1 ? innerW / (n - 1) : 0;

    const pts = series.map((p, i) => {
      const raw = Number(p?.value);
      const value = Number.isFinite(raw) ? raw : min;
      const x = CHART_PAD_L + (n > 1 ? i * stepX : innerW / 2);
      const y = CHART_PAD_T + innerH - ((value - min) / span) * innerH;
      return { x, y, point: p };
    });

    const polyline = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

    // Y axis ticks (min, mid, max)
    const yTicks = [min, (min + max) / 2, max].map((val) => ({
      val,
      y: CHART_PAD_T + innerH - ((val - min) / span) * innerH,
    }));

    // X axis labels: first, middle, last (avoid clutter)
    const xLabelIdx = n > 1 ? [0, Math.floor((n - 1) / 2), n - 1] : [0];
    const xLabels = Array.from(new Set(xLabelIdx)).map((i) => ({
      x: pts[i].x,
      label: formatShortDate(String(series[i]?.date ?? '')),
    }));

    return { pts, polyline, yTicks, xLabels, innerH };
  }, [series]);

  if (!isOpen) return null;

  // Portal to body: escapes transformed ancestors so the fixed backdrop covers
  // the entire screen. Must sit above MapStaticObjectModal City Hall (z-10050)
  // as well as in-screen CityHall layers (~10005).
  return createPortal(
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      variant="dialog"
      className="max-w-[560px]"
      showBackButton={true}
      title={title}
      backdropClassName="z-[10060]"
    >
      {currentValue !== undefined && (
        <div
          className="mb-lg text-center"
        >
          <div
            className="[font-family:'Rubik_One',sans-serif] text-[34px] leading-[1.1]"
            style={{
              color,
            }}
          >
            {formatValue(currentValue)}
          </div>
        </div>
      )}

      {description && (
        <div
          className="mb-xl text-center [font-family:Lato,sans-serif] text-[14px] leading-[1.5] text-chart-muted-text"
        >
          {description}
        </div>
      )}

      <div
        className="mb-2xl rounded-lg border border-metric-chart-modal-border bg-metric-chart-modal-surface p-lg"
      >
        {chart ? (
          <svg
            viewBox={`0 0 ${CHART_W} ${CHART_H}`}
            width="100%"
            preserveAspectRatio="xMidYMid meet"
            className="block"
          >
            {/* Horizontal gridlines + Y labels */}
            {chart.yTicks.map((tick, i) => (
              <g key={`y-${i}`}>
                <line
                  x1={CHART_PAD_L}
                  y1={tick.y}
                  x2={CHART_W - CHART_PAD_R}
                  y2={tick.y}
                  stroke="var(--color-metric-chart-modal-fill)"
                  strokeWidth={1}
                />
                <text
                  x={CHART_PAD_L - 6}
                  y={tick.y + 3}
                  textAnchor="end"
                  fontSize={10}
                  fill="var(--color-metric-chart-modal-fill-2)"
                  fontFamily="Lato, sans-serif"
                >
                  {formatValue(tick.val)}
                </text>
              </g>
            ))}

            {/* The data line */}
            {chart.pts.length > 1 && (
              <polyline
                points={chart.polyline}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Data points */}
            {chart.pts.map((p, i) => (
              <circle key={`pt-${i}`} cx={p.x} cy={p.y} r={2.5} fill={color} />
            ))}

            {/* X axis labels */}
            {chart.xLabels.map((lbl, i) => (
              <text
                key={`x-${i}`}
                x={lbl.x}
                y={CHART_H - 8}
                textAnchor="middle"
                fontSize={10}
                fill="var(--color-metric-chart-modal-fill-2)"
                fontFamily="Lato, sans-serif"
              >
                {lbl.label}
              </text>
            ))}
          </svg>
        ) : (
          <div
            className="py-[40px] text-center [font-family:Lato,sans-serif] text-[14px] text-metric-chart-modal-fill-2"
          >
            {t("No history data yet. Check back later.")}
          </div>
        )}
      </div>

      {actionLabel && onAction && (
        <button
          onClick={onAction}
          className="block w-full cursor-pointer border-none bg-transparent pb-xl [font-family:Lato,sans-serif] text-[15px] font-semibold underline"
          style={{
            color,
          }}
        >
          {actionLabel}
        </button>
      )}

      <button
        onClick={onClose}
        className="w-full cursor-pointer rounded-md border border-captcha-modal-border bg-elections-info-modal-surface px-2xl py-[14px] [font-family:Lato,sans-serif] text-[16px] font-medium text-white"
      >
        {t("Close")}
      </button>
    </ModalFrame>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Metric tile (compact 2×2 grid tile: label + value; tap opens the chart modal)
// ---------------------------------------------------------------------------

interface MetricTileProps {
  label: string;
  /** Short explanation shown as the description inside the chart modal. */
  hint: string;
  /** Icon image src URL rendered in the tile instead of the text label. */
  icon: string;
  value: number | undefined;
  series: MetricHistoryPoint[];
  formatValue?: (v: number) => string;
  color?: string;
  /** Optional text-link action rendered in the popup (e.g. "Go to a free plot"). */
  actionLabel?: string;
  onAction?: () => void;
}

/** Build a polyline points string from the series for the mini sparkline. */
function buildSparklinePoints(series: MetricHistoryPoint[]): string {
  const values = series.map((p) => Number(p?.value)).filter((v) => Number.isFinite(v));
  if (values.length === 0) return '';
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    min = min - 1;
    max = max + 1;
  }
  const range = max - min;
  const n = series.length;
  if (n < 2) return '';
  return series
    .map((p, i) => {
      const raw = Number(p?.value);
      const value = Number.isFinite(raw) ? raw : min;
      const x = (i / (n - 1)) * 100;
      const y = 22 - ((value - min) / range) * 18 - 2; // 2px top/bottom padding
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

export function MetricTile({
  label,
  hint,
  icon,
  value,
  series,
  formatValue = (v) => String(Math.round(v * 100) / 100),
  color = 'var(--color-brand)',
  actionLabel,
  onAction,
}: MetricTileProps) {
  const [chartOpen, setChartOpen] = useState(false);

  const sparklinePoints = series.length >= 2 ? buildSparklinePoints(series) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setChartOpen(true)}
        className="box-border flex h-full w-full cursor-pointer flex-col items-stretch justify-center gap-[6px] rounded-md border border-metric-chart-modal-border-2 bg-metric-chart-modal-surface-2 px-lg py-[10px] text-left"
      >
        {/* Icon on the left, value on the right */}
        <div className="flex items-center gap-[10px]">
          <img
            src={icon}
            alt={label}
            className="h-[34px] w-[34px] shrink-0 object-contain"
          />
          <div
            className="[font-family:'Rubik_One',sans-serif] text-[18px] leading-[1.1] font-bold text-deep-blue"
          >
            {value !== undefined ? formatValue(value) : '—'}
          </div>
        </div>

        {/* Mini sparkline preview */}
        {sparklinePoints && (
          <svg
            viewBox="0 0 100 22"
            width="100%"
            height="22"
            preserveAspectRatio="none"
            className="block"
          >
            <polyline
              points={sparklinePoints}
              stroke={color}
              strokeWidth="1.5"
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
      </button>

      <MetricChartModal
        isOpen={chartOpen}
        onClose={() => setChartOpen(false)}
        title={label}
        description={hint}
        series={series}
        currentValue={value}
        formatValue={formatValue}
        color={color}
        actionLabel={actionLabel}
        onAction={onAction ? () => { setChartOpen(false); onAction(); } : undefined}
      />
    </>
  );
}

export default MetricChartModal;
